import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { api, Severity, Source, type GridQuery } from '../api/client'
import { useBodyScrollLock } from '../hooks/useBodyScrollLock'
import { SearchSelect } from './SearchSelect'
import { vehicleTypeOptions } from './referenceOptions'
import './AdvancedFilter.css'

const SEVERITY_OPTIONS = [
  { value: Severity.Expired, label: 'Expired' },
  { value: Severity.Unknown, label: 'Unknown' },
  { value: Severity.DueSoon, label: 'Due soon' },
  { value: Severity.Valid, label: 'Valid' },
  { value: Severity.NoExpiry, label: 'Never expires' },
  { value: Severity.NotApplicable, label: 'Not applicable' },
]

export function AdvancedFilter({
  current,
  onApply,
  onClose,
}: {
  current: GridQuery
  onApply: (query: GridQuery) => void
  onClose: () => void
}) {
  const [draft, setDraft] = useState<GridQuery>(current)

  useBodyScrollLock()

  const reference = useQuery({
    queryKey: ['reference'],
    queryFn: () => api.getReference(),
    staleTime: 30 * 60 * 1000,
  })

  // Absent until a registry sweep has run. The registry block is hidden entirely
  // then, rather than rendered with pickers that cannot return anything.
  const facets = reference.data?.registry

  function toggleSite(siteId: number) {
    const selected = new Set(draft.siteIds ?? [])
    if (selected.has(siteId)) selected.delete(siteId)
    else selected.add(siteId)

    setDraft({ ...draft, siteIds: selected.size ? [...selected] : undefined })
  }

  return (
    <>
      <div className="scrim" onClick={onClose} aria-hidden="true" />

      <div className="modal" role="dialog" aria-modal="true" aria-label="Advanced filters">
        <header className="modal__head">
          <h2>Filters</h2>
          <button type="button" className="drawer__close" onClick={onClose} aria-label="Close">
            ×
          </button>
        </header>

        <div className="modal__body">
          <section className="filter">
            <h3 className="eyebrow">Overall state</h3>
            <p className="filter__hint">
              The single worst state on the vehicle. These bands don't overlap, so the
              counts add up to the whole fleet.
            </p>
            <div className="chips">
              <button
                type="button"
                className={`fchip ${draft.worstSeverity === undefined ? 'fchip--on' : ''}`}
                onClick={() => setDraft({ ...draft, worstSeverity: undefined })}
              >
                Any
              </button>
              {SEVERITY_OPTIONS.map((o) => (
                <button
                  key={o.value}
                  type="button"
                  className={`fchip ${draft.worstSeverity === o.value ? 'fchip--on' : ''}`}
                  onClick={() => setDraft({ ...draft, worstSeverity: o.value })}
                >
                  {o.label}
                </button>
              ))}
            </div>
          </section>

          <section className="filter">
            <h3 className="eyebrow">Has any obligation that is…</h3>
            <p className="filter__hint">
              Matches regardless of the vehicle's worst state — use this to find
              everything with something due soon, even when it's already overdue on
              something else.
            </p>
            <div className="chips">
              <button
                type="button"
                className={`fchip ${draft.hasSeverity === undefined ? 'fchip--on' : ''}`}
                onClick={() => setDraft({ ...draft, hasSeverity: undefined })}
              >
                Any
              </button>
              {SEVERITY_OPTIONS.map((o) => (
                <button
                  key={o.value}
                  type="button"
                  className={`fchip ${draft.hasSeverity === o.value ? 'fchip--on' : ''}`}
                  onClick={() => setDraft({ ...draft, hasSeverity: o.value })}
                >
                  {o.label}
                </button>
              ))}
            </div>
          </section>

          <section className="filter">
            <h3 className="eyebrow">Vehicle type</h3>
            {/* The custom picker, like the register's — a native popup draws its
                own blue highlight that no stylesheet can reach, and 20 types is
                past the point where typing beats scrolling. */}
            <SearchSelect
              options={vehicleTypeOptions(reference.data).filter((o) => o.hint !== 'machinery')}
              value={draft.vehicleTypeId ?? ''}
              onChange={(vehicleTypeId) =>
                setDraft({
                  ...draft,
                  vehicleTypeId: vehicleTypeId === '' ? undefined : vehicleTypeId,
                })
              }
              emptyLabel="All types"
              placeholder="Search types…"
            />
          </section>

          <section className="filter">
            <h3 className="eyebrow">Sites</h3>
            <p className="filter__hint">Pick any number. None selected means all sites.</p>
            <div className="chips">
              {reference.data?.sites?.map((s) => (
                <button
                  key={s.siteId}
                  type="button"
                  className={`fchip ${draft.siteIds?.includes(s.siteId!) ? 'fchip--on' : ''}`}
                  onClick={() => toggleSite(s.siteId!)}
                >
                  {s.name}
                </button>
              ))}
            </div>
          </section>

          <section className="filter">
            <h3 className="eyebrow">Data source</h3>
            <div className="chips">
              <button
                type="button"
                className={`fchip ${draft.source === undefined ? 'fchip--on' : ''}`}
                onClick={() => setDraft({ ...draft, source: undefined })}
              >
                Any
              </button>
              <button
                type="button"
                className={`fchip ${draft.source === Source.RegistryApi ? 'fchip--on' : ''}`}
                onClick={() => setDraft({ ...draft, source: Source.RegistryApi })}
              >
                Has RTO data
              </button>
              <button
                type="button"
                className={`fchip ${draft.source === Source.ExcelImport ? 'fchip--on' : ''}`}
                onClick={() => setDraft({ ...draft, source: Source.ExcelImport })}
              >
                Never synced with RTO
              </button>
            </div>
          </section>

          {/*
            What VAHAN says about the vehicle itself.

            Options come from the data, never a hardcoded list: a fuel type nobody
            anticipated has to be filterable, and a fixed list would start disagreeing
            with the register the moment VAHAN spells one differently.

            The whole block is absent until a sweep has run - offering an empty "Fuel"
            picker advertises a filter that cannot return anything.
          */}
          {facets && (
            <>
              {facets.rcStatuses && facets.rcStatuses.length > 0 && (
                <section className="filter">
                  <h3 className="eyebrow">RC status</h3>
                  <div className="chips">
                    <button
                      type="button"
                      className={`fchip ${draft.rcStatus === undefined ? 'fchip--on' : ''}`}
                      onClick={() => setDraft({ ...draft, rcStatus: undefined })}
                    >
                      Any
                    </button>
                    {facets.rcStatuses.map((f) => (
                      <button
                        key={f.value}
                        type="button"
                        className={`fchip ${draft.rcStatus === f.value ? 'fchip--on' : ''}`}
                        onClick={() => setDraft({ ...draft, rcStatus: f.value })}
                      >
                        {f.value}
                        <span className="fchip__n">{f.vehicleCount}</span>
                      </button>
                    ))}
                  </div>
                </section>
              )}

              {facets.fuelTypes && facets.fuelTypes.length > 0 && (
                <section className="filter">
                  <h3 className="eyebrow">Fuel</h3>
                  <div className="chips">
                    <button
                      type="button"
                      className={`fchip ${draft.fuelType === undefined ? 'fchip--on' : ''}`}
                      onClick={() => setDraft({ ...draft, fuelType: undefined })}
                    >
                      Any
                    </button>
                    {facets.fuelTypes.map((f) => (
                      <button
                        key={f.value}
                        type="button"
                        className={`fchip ${draft.fuelType === f.value ? 'fchip--on' : ''}`}
                        onClick={() => setDraft({ ...draft, fuelType: f.value })}
                      >
                        {f.value}
                        <span className="fchip__n">{f.vehicleCount}</span>
                      </button>
                    ))}
                  </div>
                </section>
              )}

              {facets.emissionNorms && facets.emissionNorms.length > 0 && (
                <section className="filter">
                  <h3 className="eyebrow">Emission norms</h3>
                  <div className="chips">
                    <button
                      type="button"
                      className={`fchip ${draft.emissionNorms === undefined ? 'fchip--on' : ''}`}
                      onClick={() => setDraft({ ...draft, emissionNorms: undefined })}
                    >
                      Any
                    </button>
                    {facets.emissionNorms.map((f) => (
                      <button
                        key={f.value}
                        type="button"
                        className={`fchip ${draft.emissionNorms === f.value ? 'fchip--on' : ''}`}
                        onClick={() => setDraft({ ...draft, emissionNorms: f.value })}
                      >
                        {f.value}
                        <span className="fchip__n">{f.vehicleCount}</span>
                      </button>
                    ))}
                  </div>
                </section>
              )}

              <section className="filter">
                <h3 className="eyebrow">Finance</h3>
                <div className="chips">
                  {/*
                    Three states, not two. "Not financed" is something the registry
                    asserted; a vehicle it has never been asked about said nothing at
                    all, and folding those together reports them as owned outright.
                  */}
                  <button
                    type="button"
                    className={`fchip ${draft.isFinanced === undefined ? 'fchip--on' : ''}`}
                    onClick={() => setDraft({ ...draft, isFinanced: undefined })}
                  >
                    Any
                  </button>
                  <button
                    type="button"
                    className={`fchip ${draft.isFinanced === true ? 'fchip--on' : ''}`}
                    onClick={() => setDraft({ ...draft, isFinanced: true })}
                  >
                    Financed
                  </button>
                  <button
                    type="button"
                    className={`fchip ${draft.isFinanced === false ? 'fchip--on' : ''}`}
                    onClick={() => setDraft({ ...draft, isFinanced: false })}
                  >
                    Owned outright
                  </button>
                </div>
              </section>

              {facets.financers && facets.financers.length > 0 && (
                <section className="filter">
                  <h3 className="eyebrow">Financer</h3>
                  <SearchSelect
                    options={facets.financers.map((f) => ({
                      value: f.value ?? '',
                      label: f.value ?? '',
                      hint: `${f.vehicleCount}`,
                    }))}
                    value={draft.financer ?? ''}
                    onChange={(financer) =>
                      setDraft({
                        ...draft,
                        financer: financer === '' ? undefined : String(financer),
                      })
                    }
                    emptyLabel="Any financer"
                    placeholder="Search financers..."
                  />
                </section>
              )}
            </>
          )}

          <section className="filter">
            <label className="filter__check">
              <input
                type="checkbox"
                checked={draft.onlyOverridden ?? false}
                onChange={(e) =>
                  setDraft({ ...draft, onlyOverridden: e.target.checked || undefined })
                }
              />
              <span>Only vehicles with hand-corrected values</span>
            </label>
          </section>
        </div>

        <footer className="modal__foot">
          <button
            type="button"
            className="btn"
            onClick={() =>
              setDraft({ page: 1, pageSize: current.pageSize, search: current.search })
            }
          >
            Clear all
          </button>
          <button
            type="button"
            className="btn btn--primary"
            onClick={() => {
              onApply({ ...draft, page: 1 })
              onClose()
            }}
          >
            Apply filters
          </button>
        </footer>
      </div>
    </>
  )
}
